/* ==========================================================================
   Metic — theme behaviour
   Custom elements only; no external dependencies.
   ========================================================================== */
(function () {
  'use strict';

  const Theme = window.Theme || {};
  const routes = Theme.routes || {};
  // `routes.root_url` is "/" on the primary locale and "/en" — with no trailing
  // slash — on every other one. Four call sites concatenated a path straight
  // onto it, producing "/enproducts/<handle>", which 404s. That took out quick
  // view, recently viewed and the shipping estimator on every locale except the
  // default, and the quick view's own "view full details" fallback link with
  // them. Normalise once here rather than remembering the slash four times.
  const rootUrl = (routes.root || '/').replace(/\/?$/, '/');
  const strings = Theme.strings || {};
  const settings = Theme.settings || {};

  /* ---------------------------------------------------------------- utils */

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const formatMoney = (cents) => {
    // Mirror Liquid: `money_with_currency` when the shop shows the currency code.
    const format =
      (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) ||
      settings.moneyFormat ||
      '${{amount}}';
    const parts = (cents / 100).toFixed(2).split('.');
    const rounded = String(Math.round(cents / 100));
    const group = (digits, separator) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, group(parts[0], ',') + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, group(rounded, ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, group(parts[0], '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, group(rounded, '.'));
  };

  const esc = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );

  const fetchJSON = (url, options) =>
    fetch(url, options).then((r) => {
      if (!r.ok) return r.json().then((body) => Promise.reject(body));
      return r.json();
    });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [contenteditable], [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container) => {
    // Resolved per keystroke: drawer bodies are re-rendered while the trap is live,
    // so a list captured at open time would point at detached nodes.
    const items = () =>
      Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const list = items();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    const current = items();
    if (current.length) {
      current[0].focus({ preventScroll: true });
    } else {
      container.setAttribute('tabindex', '-1');
      container.focus({ preventScroll: true });
    }
    return () => container.removeEventListener('keydown', handler);
  };

  // Off-screen polite region for content that changes without a visible message
  let liveRegion = null;
  const announce = (message) => {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 80);
  };

  const storage = {
    get(key, fallback) {
      try {
        const raw = localStorage.getItem('metic:' + key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem('metic:' + key, JSON.stringify(value));
      } catch (e) {
        /* quota or private mode — non-fatal */
      }
    },
  };

  const icons = {
    check:
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10.5L8 14.5L16 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    alert:
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.6"/><path d="M10 6v5M10 14h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    pause:
      '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="3" height="10" rx="1"/><rect x="11" y="5" width="3" height="10" rx="1"/></svg>',
    play: '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M7 5.5l7 4.5-7 4.5z"/></svg>',
    close:
      '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  };

  // Theme.strings carries translated text with a [token] placeholder, the same shape the
  // pagination and shipping-bar strings use.
  /*
   * Liquid's `t` filter HTML-escapes what it returns, so a French string carrying an
   * apostrophe arrives as "n&#39;existe pas". The toast writes with textContent, which
   * shows that literally — decoded here, in the one helper that feeds it.
   */
  const decodeEntities = (value) => {
    if (!value || value.indexOf('&') === -1) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  };

  const say = (key, code) => decodeEntities((strings[key] || key).replace('[code]', code));

  const sayWith = (key, tokens) =>
    decodeEntities(
      Object.keys(tokens).reduce(
        (out, token) => out.split('[' + token + ']').join(tokens[token]),
        strings[key] || key
      )
    );

  const toast = (message, type = 'success') => {
    const region = document.getElementById('ToastRegion');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML = (type === 'error' ? icons.alert : icons.check) + '<span></span>';
    el.querySelector('span').textContent = message;
    region.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity .3s';
      setTimeout(() => el.remove(), 300);
    }, 3200);
  };

  /* --------------------------------------------------------------- events */

  const bus = new EventTarget();
  const emit = (name, detail) => bus.dispatchEvent(new CustomEvent(name, { detail }));
  const on = (name, fn) => bus.addEventListener(name, fn);
  window.MeticBus = { emit, on };

  /* ------------------------------------------------------- <reveal-group> */

  if (settings.revealOnScroll && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const observeReveals = (root = document) =>
      root.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => revealObserver.observe(el));
    document.addEventListener('DOMContentLoaded', () => observeReveals());
    observeReveals();
    on('section:loaded', (e) => observeReveals(e.detail.target || document));
  } else {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-revealed'));
  }

  /* ----------------------------------------------------------- <x-drawer> */

  class XDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.lastActive = null;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__overlay')) {
          this.close();
        }
      });
      this.escHandler = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.escHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.escHandler);
    }

    open() {
      // Re-opening an open drawer would stack a second focus trap and overwrite the
      // remover for the first. `cart:added` re-opens the cart drawer on every add,
      // including adds made from controls that already live inside it.
      if (this.classList.contains('is-open')) return;
      this.lastActive = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      const panel = this.querySelector('.drawer__panel');
      if (panel) this.releaseFocus = trapFocus(panel);
      emit('drawer:open', { drawer: this });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('x-drawer.is-open')) document.body.classList.remove('drawer-open');
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastActive) this.lastActive.focus({ preventScroll: true });
      emit('drawer:close', { drawer: this });
    }

    toggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }
  }
  customElements.define('x-drawer', XDrawer);

  const openDrawer = (id) => {
    const el = document.getElementById(id);
    if (el && typeof el.open === 'function') el.open();
    return el;
  };
  window.MeticDrawer = { open: openDrawer };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-drawer-open');
    const drawer = document.getElementById(id);
    if (!drawer) return;
    e.preventDefault();
    drawer.open();
  });

  /*
   * Native smooth scrolling never completes inside these snap viewports — verified in the
   * browser: `scrollTo({behavior:'smooth'})` leaves scrollLeft at 0 under both mandatory and
   * proximity snap, while a direct assignment lands correctly. So drive the animation
   * ourselves, one assignment per frame, which the snap container accepts.
   */

  const smoothScrollX = (el, target) => {
    const max = el.scrollWidth - el.clientWidth;
    const to = Math.max(0, Math.min(max, Math.round(target)));
    if (el.scrollLeft === to) return;
    /*
     * Animate where the browser will, but never depend on it. Smooth scrolling is driven by
     * animation frames, which are paused in a background tab — so a short settle pass
     * guarantees the final position regardless of whether the animation ever ran.
     */
    try {
      el.scrollTo({ left: to, behavior: 'smooth' });
    } catch (e) {
      el.scrollLeft = to;
    }
    setTimeout(() => {
      if (Math.abs(el.scrollLeft - to) > 2) el.scrollLeft = to;
    }, 600);
  };

  /* --------------------------------------------------------- <x-carousel> */

  class XCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.carousel__viewport');
      if (!this.viewport) return;
      // Controls usually sit inside, but a section may render them in its own header.
      const controlScope = this.closest('[data-carousel-scope]') || this;
      this.prevBtn = this.querySelector('[data-carousel-prev]') || controlScope.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]') || controlScope.querySelector('[data-carousel-next]');
      this.nav = this.querySelector('.carousel__nav');
      this.pagerCurrent = controlScope.querySelector('[data-carousel-current]');
      this.pagerTotal = controlScope.querySelector('[data-carousel-total]');
      this.dots = this.querySelectorAll('[data-carousel-dot]');
      this.progressBar = this.querySelector('.carousel__progress-bar');

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.viewport.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });

      /*
       * Sit exactly on a slide once movement stops.
       *
       * Aiming a scroll at the right pixel is not enough on its own: a smooth animation
       * can be cut short, a mandatory snap can settle a fraction out, a lazily-loaded
       * image can reflow mid-flight, and an autoplay tick can arrive on top of any of
       * them. Rather than try to predict each of those, measure once the dust settles and
       * correct. The debounce is longer than the gap between animation frames, so this
       * cannot fire mid-animation and yank the slide — it only runs when the scroll has
       * genuinely come to rest.
       */
      this.viewport.addEventListener('scroll', debounce(() => this.settleOnSlide(), 180), { passive: true });
      // A ResizeObserver also catches a hidden tab panel becoming visible, which a window
      // resize listener never sees — otherwise both arrows stay disabled at 0 width.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(debounce(() => this.update(), 60));
        this.resizeObserver.observe(this.viewport);
      } else {
        this.onResize = debounce(() => this.update(), 150);
        window.addEventListener('resize', this.onResize);
      }
      this.update();

      this.setupAutoplay();
      this.setupDrag();
    }

    disconnectedCallback() {
      this.stopAuto();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    setupAutoplay() {
      this.interval = parseInt(this.dataset.autoplay || '0', 10);
      this.paused = false;
      if (!(this.interval > 0)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      ['mouseenter', 'focusin'].forEach((evt) => this.addEventListener(evt, () => this.stopAuto()));
      this.addEventListener('mouseleave', () => this.startAuto());
      this.addEventListener('focusout', (e) => {
        // focusout also fires when focus moves between two children
        if (!this.contains(e.relatedTarget)) this.startAuto();
      });
      this.onVisibility = () => (document.hidden ? this.stopAuto() : this.startAuto());
      document.addEventListener('visibilitychange', this.onVisibility);
      this.addPauseButton();
      this.startAuto();
    }

    // WCAG 2.2.2 — moving content needs a control that stops it for good
    addPauseButton() {
      const host = this.querySelector('.carousel__dots') || this.querySelector('.carousel__nav');
      if (!host || this.querySelector('[data-carousel-pause]')) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'icon-btn carousel__pause';
      button.style.setProperty('--icon-btn-size', '3.2rem');
      button.setAttribute('data-carousel-pause', '');
      button.innerHTML = icons.pause;
      button.setAttribute('aria-label', strings.sliderPause || 'Pause slideshow');
      button.addEventListener('click', () => {
        this.paused = !this.paused;
        button.innerHTML = this.paused ? icons.play : icons.pause;
        button.setAttribute(
          'aria-label',
          this.paused ? strings.sliderPlay || 'Play slideshow' : strings.sliderPause || 'Pause slideshow'
        );
        this.paused ? this.stopAuto() : this.startAuto();
      });
      host.appendChild(button);
    }

    startAuto() {
      if (!this.interval || this.paused || document.hidden) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        const atEnd = this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 4;
        atEnd ? this.goTo(0) : this.scrollByPage(1);
      }, this.interval);
    }

    stopAuto() {
      clearInterval(this.timer);
    }

    // Drag-to-scroll on pointer devices
    setupDrag() {
      let down = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;
      let captured = 0;
      this.viewport.addEventListener('pointerdown', (e) => {
        // Re-armed for every pointer type: a touch tap must not inherit the guard left
        // behind by a mouse drag that was released outside the viewport.
        moved = false;
        if (e.pointerType !== 'mouse') return;
        down = true;
        this.dragging = true;
        startX = e.clientX;
        startScroll = this.viewport.scrollLeft;
        // scroll-behavior: smooth would animate every step of the drag
        this.viewport.style.scrollBehavior = 'auto';
      });
      this.viewport.addEventListener('pointermove', (e) => {
        if (!down) return;
        if (!moved && Math.abs(e.clientX - startX) > 4) {
          moved = true;
          /*
           * Capture only once a drag is genuinely under way, never on plain mousedown.
           * A captured pointer retargets the following `click` to the capture element, so
           * capturing every press made every link inside a carousel unclickable — the
           * anchor never became the click target. Deferring it keeps drag-past-the-edge
           * working while leaving ordinary clicks completely untouched.
           */
          this.viewport.setPointerCapture(e.pointerId);
          captured = e.pointerId;
        }
        this.viewport.scrollLeft = startScroll - (e.clientX - startX);
      });
      ['pointerup', 'pointercancel'].forEach((evt) =>
        this.viewport.addEventListener(evt, (e) => {
          down = false;
          this.dragging = false;
          this.viewport.style.scrollBehavior = '';
          if (captured && this.viewport.hasPointerCapture(e.pointerId)) {
            this.viewport.releasePointerCapture(e.pointerId);
          }
          captured = 0;
        })
      );
      // Swallow the click that ends a drag so it does not open the card underneath.
      // `detail === 0` marks a click that no pointer produced — Enter/Space on a focused
      // button, or an .click() call — which must never be treated as the tail of a drag.
      this.viewport.addEventListener(
        'click',
        (e) => {
          if (!moved || e.detail === 0) return;
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        },
        true
      );
    }

    get slideWidth() {
      const slide = this.viewport.querySelector('.carousel__slide');
      if (!slide) return this.viewport.clientWidth;
      const gap = parseFloat(getComputedStyle(this.viewport).columnGap || '0');
      return slide.getBoundingClientRect().width + gap;
    }

    scrollByPage(direction) {
      const perPage = this.dataset.scrollBy === 'page' ? Math.max(1, Math.round(this.viewport.clientWidth / this.slideWidth)) : 1;
      /*
       * Target the slide grid, not the current scroll offset.
       *
       * This used to advance from `scrollLeft` directly. Any time that value was not
       * exactly on a slide boundary — autoplay firing while the previous smooth scroll
       * was still animating, a snap still settling, a trackpad nudge — the offset was
       * added to the destination and carried forward for the rest of the session,
       * because nothing ever measured back to the grid. Reproduced on the home page
       * banner slider: slides at 0 / 2320 / 4640, and a scrollLeft of 2368.5, leaving
       * slide two 48.5px to the left with slide three showing at the right edge.
       *
       * Rounding to the nearest slide first makes every move land on a boundary and
       * discards whatever drift had accumulated.
       */
      const current = this.slideWidth ? Math.round(this.viewport.scrollLeft / this.slideWidth) : 0;
      smoothScrollX(this.viewport, this.slideOffset(current + direction * perPage));
      // smoothScrollX assigns synchronously, so refresh the controls now rather than
      // waiting on the debounced scroll listener, which does not fire reliably for a
      // carousel injected after load (cart recommendations).
      this.update();
    }

    goTo(index) {
      smoothScrollX(this.viewport, this.slideOffset(index));
      this.update();
    }

    /*
     * Where a slide actually starts, read off the DOM, rather than index x slideWidth.
     *
     * `slideWidth` is measured live, so a call made while the layout is momentarily
     * different — a scrollbar appearing, a lazily-loaded image settling — multiplies that
     * transient value into a destination no slide occupies. Caught on the home page
     * banner slider: slides at 0 / 2320 / 4640 and a scroll target of 2368.5, which left
     * slide two 48.5px to the left with slide three showing at the right edge. It came
     * and went with the timing, which is what made it look intermittent.
     *
     * `offsetLeft` is the position itself, so it cannot disagree with where the slide is.
     */
    settleOnSlide() {
      // Never fight a finger or a held mouse button.
      if (this.dragging) return;
      const width = this.slideWidth;
      if (!width) return;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      // The ends are already exact by clamping; correcting them can only cause a fight.
      if (this.viewport.scrollLeft <= 1 || this.viewport.scrollLeft >= max - 1) return;
      const target = this.slideOffset(Math.round(this.viewport.scrollLeft / width));
      if (Math.abs(this.viewport.scrollLeft - target) > 1) {
        this.viewport.scrollLeft = target;
        this.update();
      }
    }

    slideOffset(index) {
      const slides = this.viewport.querySelectorAll('.carousel__slide');
      if (!slides.length) return 0;
      const i = Math.max(0, Math.min(slides.length - 1, index));
      return slides[i].offsetLeft;
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const max = scrollWidth - clientWidth;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft >= max - 2;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      /*
       * Hide the whole control when there is nothing to scroll. Sections cannot decide this
       * in Liquid — whether the slides overflow depends on the viewport, not on the block
       * count — so the carousel resolves it at measure time. clientWidth is 0 while the
       * carousel sits in a hidden tab panel; treat that as "unknown" and leave the nav as-is
       * rather than hiding it based on a meaningless measurement.
       */
      if (this.nav && clientWidth > 0) this.nav.hidden = max <= 1;
      if (this.dots.length) {
        const index = Math.round(scrollLeft / this.slideWidth);
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }

      // Numeric pager ("2/4"). The controls may live outside the carousel, so look them
      // up from the closest shared ancestor rather than from `this`.
      if (this.pagerCurrent) {
        const slides = this.viewport.querySelectorAll('.carousel__slide').length;
        const index = this.slideWidth ? Math.round(scrollLeft / this.slideWidth) : 0;
        this.pagerCurrent.textContent = Math.min(slides, index + 1);
        if (this.pagerTotal) this.pagerTotal.textContent = slides;
      }
      if (this.progressBar) {
        const ratio = max > 0 ? Math.min(1, (scrollLeft + clientWidth) / scrollWidth) : 1;
        this.progressBar.style.transform = `scaleX(${ratio})`;
      }
    }
  }
  customElements.define('x-carousel', XCarousel);

  /* ------------------------------------------------------------- <x-tabs> */

  class XTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.select(next);
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      // A carousel measured while its panel was hidden reports zero width
      if (this.panels[index]) {
        this.panels[index].querySelectorAll('x-carousel').forEach((carousel) => {
          if (carousel.viewport && typeof carousel.update === 'function') carousel.update();
        });
      }
      this.dispatchEvent(new CustomEvent('tab:change', { detail: { index } }));
    }
  }
  customElements.define('x-tabs', XTabs);

  /* ----------------------------------------------------------- <x-header> */

  // Mega panels are positioned entirely in CSS against the nav row, so they are in the
  // right place for keyboard users and on first paint — see .nav__panel--mega in base.css
  class XHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      if (!this.header) return;
      this.onScroll = () => {
        this.header.classList.toggle('is-stuck', window.scrollY > 80);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('x-header', XHeader);

  /* ------------------------------------------------- <x-predictive-search> */

  class XPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results) return;
      this.abort = null;

      // Combobox semantics, applied here so the markup stays a plain search field
      if (!this.results.id) this.results.id = `PredictiveResults-${this.input.id || 'search'}`;
      this.results.setAttribute('role', 'listbox');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');

      this.input.addEventListener(
        'input',
        debounce(() => this.search(), 260)
      );
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length > 1) this.search();
        else this.showDefault();
      });
      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.hide();
      };
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    disconnectedCallback() {
      if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      if (this.abort) this.abort.abort();
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(e) {
      if (e.key === 'Escape') return this.hide();
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const options = this.results.hidden ? [] : this.options;
      if (!options.length) return;
      e.preventDefault();
      const current = options.indexOf(document.activeElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = current === -1 ? (step === 1 ? 0 : options.length - 1) : current + step;
      if (next < 0) {
        this.input.focus();
        return;
      }
      options[Math.min(next, options.length - 1)].focus();
    }

    // Every result link is an option so the arrow keys have something to walk
    markOptions() {
      this.results.querySelectorAll('.predictive-search__group').forEach((group) => {
        const heading = group.querySelector('.predictive-search__heading');
        group.setAttribute('role', 'group');
        if (heading) group.setAttribute('aria-label', heading.textContent.trim());
      });
      this.results.querySelectorAll('a').forEach((link) => {
        link.setAttribute('role', 'option');
        link.tabIndex = -1;
      });
    }

    reveal() {
      this.markOptions();
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    showDefault() {
      const template = this.querySelector('[data-predictive-default]');
      // No suggestions configured — close rather than leave stale results up
      if (!template) return this.hide();
      this.results.innerHTML = template.innerHTML;
      this.reveal();
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    search() {
      const term = this.input.value.trim();
      if (term.length < 2) return this.showDefault();
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const url = `${routes.predictive_search_url}?q=${encodeURIComponent(
        term
      )}&resources[type]=product,collection,article,query&resources[limit]=6&section_id=predictive-search`;

      fetch(url, { signal: this.abort.signal })
        .then((r) => r.text())
        .then((text) => {
          const doc = parseHTML(text);
          const content = doc.querySelector('[data-predictive-content]');
          this.results.innerHTML = content ? content.innerHTML : '';
          this.reveal();
        })
        .catch(() => {});
    }
  }
  customElements.define('x-predictive-search', XPredictiveSearch);

  /* -------------------------------------------------------------- cart API */

  const Cart = {
    // Serialises cart writes so two fast clicks cannot race each other
    queue: Promise.resolve(),

    async add(items, extra = {}) {
      const body = { items: Array.isArray(items) ? items : [items], ...extra };
      const sections = Cart.sectionIds();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const data = await fetchJSON(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      Cart.renderSections(data.sections);
      // The item is already in the cart — do not make the drawer wait on /cart.js
      emit('cart:added', { data });
      Cart.refreshState().catch(() => {});
      return data;
    },

    async change(payload) {
      const body = { ...payload };
      const sections = Cart.sectionIds();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const data = await fetchJSON(routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      Cart.renderSections(data.sections);
      Cart.updateState(data);
      emit('cart:updated', { data });
      return data;
    },

    async update(payload) {
      const body = { ...payload };
      const sections = Cart.sectionIds();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const data = await fetchJSON(routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      Cart.renderSections(data.sections);
      Cart.updateState(data);
      emit('cart:updated', { data });
      return data;
    },

    sectionIds() {
      return Array.from(document.querySelectorAll('[data-cart-section]'))
        .map((el) => el.dataset.cartSection)
        .filter(Boolean);
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const target = document.querySelector(`[data-cart-section="${id}"]`);
        if (!target) return;
        const doc = parseHTML(sections[id]);
        const source = doc.querySelector(`[data-cart-section="${id}"]`);
        if (source) {
          target.innerHTML = source.innerHTML;
          emit('section:loaded', { target });
        }
      });
    },

    async refreshState() {
      const data = await fetchJSON(routes.cart_url + '.js');
      Cart.updateState(data);
      return data;
    },

    updateState(cart) {
      if (!cart) return;
      /*
       * renderSections has just replaced the drawer markup with the Liquid shell, which
       * blanks the discount rows — they are drawn from the cart JSON, not from Liquid.
       * This is the one point every path reaches with a full cart in hand: `add` arrives
       * through refreshState, `change` and `update` come straight here. Without it, adding
       * a product wiped the chips and with them the only way to remove a code.
       *
       * Discounts is declared further down; that is fine because nothing calls this during
       * evaluation. A typeof guard would be worse than none — on a const in its temporal
       * dead zone typeof throws rather than answering "undefined".
       */
      Discounts.render(cart);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-cart-total]').forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      Cart.updateShippingBar(cart.total_price);
    },

    updateShippingBar(totalCents) {
      const threshold = parseFloat(settings.freeShippingThreshold || 0) * 100;
      if (!threshold) return;
      document.querySelectorAll('[data-shipping-bar]').forEach((bar) => {
        const fill = bar.querySelector('.shipping-bar__fill');
        const text = bar.querySelector('.shipping-bar__text');
        const ratio = Math.min(1, totalCents / threshold);
        if (fill) fill.style.width = ratio * 100 + '%';
        if (text) {
          if (totalCents >= threshold) {
            /* `t` HTML-escapes its output, so an apostrophe arrives as &#39;. textContent
               would print that entity literally — this string is already escaped. */
            text.innerHTML = strings.shippingFree || '';
          } else {
            const remaining = formatMoney(threshold - totalCents);
            text.innerHTML = (strings.shippingRemaining || '').replace('[amount]', `<b>${remaining}</b>`);
          }
        }
      });
    },
  };
  window.MeticCart = Cart;

  on('cart:added', () => {
    if (settings.cartType === 'drawer') openDrawer('CartDrawer');
  });

  /* ------------------------------------------------- quantity steppers (all) */

  /*
   * One delegated handler drives every `{% render 'quantity-input' %}` in the theme —
   * product page, quick-order list, cart page and cart drawer. Delegation means steppers
   * inside markup that Shopify re-renders (cart sections, quick view) keep working without
   * rebinding. Consumers react to the resulting 'change' event rather than to the click.
   */
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-qty-change]');
    if (!button) return;
    const wrapper = button.closest('.qty');
    const input = wrapper && wrapper.querySelector('.qty__input');
    if (!input) return;

    e.preventDefault();

    const step = parseFloat(input.step) || 1;
    const min = input.min === '' ? 0 : parseFloat(input.min);
    const max = input.max === '' ? Infinity : parseFloat(input.max);
    const delta = parseFloat(button.dataset.qtyChange) || 0;
    const current = parseFloat(input.value) || 0;

    let next = current + delta * step;
    if (next < min) next = min;
    if (next > max) next = max;
    if (next === current) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* -------------------------------------------------------- <x-cart-item> */

  class XCartItem extends HTMLElement {
    connectedCallback() {
      this.line = this.dataset.line;
      this.key = this.dataset.key;
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.setQuantity(0);
        }
      });
      // The +/- buttons are driven by the delegated stepper handler below, which fires a
      // 'change' event on the input. Listening for that keeps one code path for every
      // stepper in the theme and avoids double-applying a delta here.
      const input = this.querySelector('.qty__input');
      if (input) {
        input.addEventListener(
          'change',
          debounce(() => this.setQuantity(parseInt(input.value, 10)), 200)
        );
      }
    }

    async setQuantity(quantity) {
      this.classList.add('is-loading');
      try {
        await (Cart.queue = Cart.queue.catch(() => {}).then(() => Cart.change({ id: this.key, quantity })));
      } catch (err) {
        toast(err && err.description ? err.description : 'Error', 'error');
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('x-cart-item', XCartItem);

  /* ------------------------------------------------------- <x-cart-notes> */

  class XCartNotes extends HTMLElement {
    connectedCallback() {
      const field = this.querySelector('textarea');
      if (!field) return;
      field.addEventListener(
        'change',
        debounce(() => Cart.update({ note: field.value }), 400)
      );
    }
  }
  customElements.define('x-cart-notes', XCartNotes);

  /* ------------------------------------------------------- <x-gift-wrap> */

  class XGiftWrap extends HTMLElement {
    connectedCallback() {
      const checkbox = this.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      checkbox.addEventListener('change', async () => {
        this.classList.add('is-loading');
        try {
          if (checkbox.checked) {
            await Cart.add({ id: parseInt(this.dataset.variantId, 10), quantity: 1 });
          } else if (this.dataset.lineKey) {
            await Cart.change({ id: this.dataset.lineKey, quantity: 0 });
          }
        } catch (e) {
          checkbox.checked = !checkbox.checked;
        } finally {
          this.classList.remove('is-loading');
        }
      });
    }
  }
  customElements.define('x-gift-wrap', XGiftWrap);

  /* --------------------------------------------------- <x-variant-picker> */

  class XVariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.url = this.dataset.url;
      this.variants = this.getVariants();
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.selectedOptions);
    }

    getVariants() {
      const script = this.querySelector('[data-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return [];
      }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions;
      // reflect the chosen value in each option label
      this.querySelectorAll('fieldset').forEach((fieldset, i) => {
        const output = fieldset.querySelector('[data-option-value]');
        if (output) output.textContent = options[i] || '';
      });

      const variant = this.variants.find((v) =>
        v.options.every((option, i) => option === options[i])
      );

      this.updateAvailability(options);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateURL(variant);
      this.updateFormInput(variant);
      this.renderSection(variant);
      emit('variant:change', { variant, section: this.section });
    }

    updateAvailability(options) {
      // An option value is unavailable when no buyable variant pairs it with the other
      // selections — every other index counts, not just the ones before it
      this.querySelectorAll('fieldset').forEach((fieldset, index) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const match = this.variants.some(
            (v) =>
              v.available &&
              v.options.every((option, i) =>
                i === index ? option === input.value : !options[i] || option === options[i]
              )
          );
          const wrapper = input.closest('.variant-button, .variant-swatch');
          if (wrapper) wrapper.classList.toggle('is-unavailable', !match);
        });
      });
    }

    updateURL(variant) {
      if (!variant || !this.url) return;
      // The quick view borrows this picker; it must not rewrite the collection URL
      if (this.closest('[data-quick-view-body]')) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }

    updateFormInput(variant) {
      document
        .querySelectorAll(`[data-product-form="${this.section}"] input[name="id"], form[data-section="${this.section}"] input[name="id"]`)
        .forEach((input) => {
          input.value = variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    setUnavailable() {
      this.scope.querySelectorAll(`[data-add-button="${this.section}"]`).forEach((btn) => {
        btn.disabled = true;
        const text = btn.querySelector('[data-add-text]') || btn;
        text.innerHTML = strings.unavailable || 'Unavailable';
      });
      // Buy-now goes away entirely: disabled is not enough, it would still check out.
      this.scope.querySelectorAll('[data-dynamic-checkout]').forEach((el) => {
        el.hidden = true;
      });
    }

    // Everything this picker rewrites lives inside its own section — or, in the quick view,
    // inside the modal body, where the section wrapper never makes it into the document
    get scope() {
      return (
        this.closest('[data-quick-view-body]') ||
        document.getElementById(`shopify-section-${this.section}`) ||
        document
      );
    }

    async renderSection(variant) {
      if (!this.url) return;
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const token = (this.token = (this.token || 0) + 1);
      this.classList.add('is-loading');
      try {
        const res = await fetch(`${this.url}?variant=${variant.id}&section_id=${this.section}`, {
          signal: this.abort.signal,
        });
        const text = await res.text();
        // A slower earlier request must never repaint over a newer one
        if (token !== this.token) return;
        const doc = parseHTML(text);
        const scope = this.scope;
        ['[data-product-price]', '[data-product-inventory]', '[data-product-sku]', '[data-product-badges]'].forEach(
          (selector) => {
            const source = doc.querySelector(`#shopify-section-${this.section} ${selector}`) || doc.querySelector(selector);
            const target = scope.querySelector(selector);
            if (source && target) target.innerHTML = source.innerHTML;
          }
        );

        const addSource =
          doc.querySelector(`#shopify-section-${this.section} [data-add-button="${this.section}"]`) ||
          doc.querySelector(`[data-add-button="${this.section}"]`);
        scope.querySelectorAll(`[data-add-button="${this.section}"]`).forEach((btn) => {
          if (!addSource) return;
          btn.disabled = addSource.hasAttribute('disabled');
          btn.innerHTML = addSource.innerHTML;
        });

        // Same for buy-now: the section rendered for this variant knows whether it is
        // buyable, so the wrapper's hidden state is copied rather than recomputed.
        const buyNowSource =
          doc.querySelector(`#shopify-section-${this.section} [data-dynamic-checkout]`) ||
          doc.querySelector('[data-dynamic-checkout]');
        if (buyNowSource) {
          scope.querySelectorAll('[data-dynamic-checkout]').forEach((el) => {
            el.hidden = buyNowSource.hasAttribute('hidden');
          });
        }

        // Switch the gallery to the variant's featured media
        if (variant.featured_media_id) {
          const media = scope.querySelector(`[data-media-id="${variant.featured_media_id}"]`);
          if (media) media.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } catch (e) {
        /* aborted or failed — keep the current render */
      } finally {
        if (token === this.token) this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('x-variant-picker', XVariantPicker);

  /* ------------------------------------------------------ <x-product-form> */

  class XProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[type="submit"]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(e) {
      if (this.form.dataset.native === 'true') return;
      e.preventDefault();
      if (this.button && this.button.disabled) return;

      const formData = new FormData(this.form);
      const item = { id: formData.get('id'), quantity: parseInt(formData.get('quantity') || '1', 10) };

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;

      this.setLoading(true);
      try {
        await Cart.add(item);
        toast(strings.added || 'Added to cart');
      } catch (err) {
        toast(err && err.description ? err.description : 'Error', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(state) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', state);
      this.button.disabled = state;
    }
  }
  customElements.define('x-product-form', XProductForm);

  /* --------------------------------------------------------- <x-quick-add> */

  class XQuickAdd extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;
        this.add(btn, variantId);
      });
    }

    async add(btn, variantId) {
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        await Cart.add({ id: parseInt(variantId, 10), quantity: 1 });
        toast(strings.added || 'Added to cart');
      } catch (err) {
        toast(err && err.description ? err.description : 'Error', 'error');
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    }
  }
  customElements.define('x-quick-add', XQuickAdd);

  /* -------------------------------------------------------- <x-quick-view> */

  function quickViewFallback(href) {
    return `<p class="subtext" style="padding:2rem 0;">
      <a class="btn btn--link" href="${href}">${strings.viewFullDetails || 'View full details'}</a>
    </p>`;
  }

  class XQuickView extends HTMLElement {
    async load(handle) {
      const drawer = document.getElementById('QuickViewModal');
      const body = drawer && drawer.querySelector('[data-quick-view-body]');
      if (!drawer || !body) return;
      if (!handle) return;
      body.innerHTML = '<div class="infinite-trigger"><span class="spinner"></span></div>';
      drawer.open();
      const href = `${rootUrl}products/${handle}`;
      try {
        const res = await fetch(`${href}?section_id=quick-view-content`);
        const doc = parseHTML(await res.text());
        const content = doc.querySelector('[data-quick-view-content]');
        // The section renders an empty marker when the URL did not resolve to a
        // product. Offer the product page rather than an empty modal, which
        // reads as the button being broken.
        const usable = content && !content.hasAttribute('data-quick-view-empty') && content.innerHTML.trim();
        body.innerHTML = usable ? content.innerHTML : quickViewFallback(href);
        emit('section:loaded', { target: body });
      } catch (e) {
        body.innerHTML = quickViewFallback(href);
      }
    }
  }
  customElements.define('x-quick-view', XQuickView);

  // Registered once, not per element, so an editor reload cannot double-fetch
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger) return;
    const host = document.querySelector('x-quick-view');
    if (!host || typeof host.load !== 'function') return;
    e.preventDefault();
    host.load(trigger.dataset.quickView);
  });

  /* -------------------------------------------------------- card swatches */

  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('.pcard .swatch[data-variant-image]');
    if (!swatch) return;
    const card = swatch.closest('.pcard');
    const img = card && card.querySelector('.pcard__img-main');
    if (!img) return;
    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.getAttribute('srcset') || '';
    }
    // The card image is served with a srcset, which wins over src until it is cleared
    img.removeAttribute('srcset');
    img.src = swatch.dataset.variantImage;
    const quickAdd = card.querySelector('[data-quick-add]');
    if (quickAdd && swatch.dataset.variantId) {
      if (!quickAdd.dataset.originalVariant) quickAdd.dataset.originalVariant = quickAdd.dataset.quickAdd;
      quickAdd.dataset.quickAdd = swatch.dataset.variantId;
    }
    card.querySelectorAll('.swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
  });

  document.addEventListener('mouseout', (e) => {
    const card = e.target.closest('.pcard');
    if (!card || (e.relatedTarget && card.contains(e.relatedTarget))) return;
    const img = card.querySelector('.pcard__img-main');
    if (img && img.dataset.originalSrc) {
      img.src = img.dataset.originalSrc;
      if (img.dataset.originalSrcset) img.setAttribute('srcset', img.dataset.originalSrcset);
    }
    const quickAdd = card.querySelector('[data-quick-add]');
    if (quickAdd && quickAdd.dataset.originalVariant) quickAdd.dataset.quickAdd = quickAdd.dataset.originalVariant;
    // Back to the checked swatch — the card renders the first value checked
    const swatches = Array.from(card.querySelectorAll('.swatch'));
    const checked = swatches.find((s) => s.getAttribute('aria-checked') === 'true') || swatches[0];
    swatches.forEach((s) => s.classList.toggle('is-active', s === checked));
  });

  /* ---------------------------------------------------------- comparison */

  const Compare = {
    key: 'compare',
    max: 4,
    get items() {
      return storage.get(Compare.key, []);
    },
    set items(value) {
      storage.set(Compare.key, value);
      Compare.render();
    },
    toggle(handle, image, title) {
      const items = Compare.items.slice();
      const index = items.findIndex((i) => i.handle === handle);
      if (index > -1) {
        items.splice(index, 1);
      } else {
        if (items.length >= Compare.max) {
          toast(strings.compareLimit || `You can compare up to ${Compare.max} products`, 'error');
          return;
        }
        items.push({ handle, image, title });
      }
      Compare.items = items;
    },
    render() {
      const items = Compare.items;
      const tray = document.getElementById('CompareTray');
      document.querySelectorAll('[data-compare-toggle]').forEach((btn) => {
        const on = items.some((i) => i.handle === btn.dataset.compareToggle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (!tray) return;

      /*
       * The tray belongs to compare MODE, not to the selection. It appears the moment the
       * toolbar switch is on — showing empty slots to fill — because a tray that only turns
       * up after the first pick gives no hint that bulk selection is available at all.
       */
      const modeOn = !document.documentElement.classList.contains('compare-controls-hidden');
      tray.classList.toggle('is-visible', modeOn);

      const count = tray.querySelector('[data-compare-count]');
      if (count) count.textContent = items.length + '/' + Compare.max;

      const viewBtn = tray.querySelector('[data-compare-view]');
      if (viewBtn) viewBtn.disabled = items.length < 2;

      const list = tray.querySelector('[data-compare-items]');
      if (!list) return;
      let html = items
        .map(
          (item) => `<div class="compare-tray__item">
            <img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy" width="64" height="64">
            <button type="button" class="compare-tray__remove" data-compare-remove="${esc(item.handle)}" aria-label="Remove">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>`
        )
        .join('');
      for (let i = items.length; i < Compare.max; i++) html += '<div class="compare-tray__slot"></div>';
      list.innerHTML = html;
    },
  };

  window.MeticCompare = Compare;

  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-compare-toggle]');
    if (toggle) {
      e.preventDefault();
      Compare.toggle(toggle.dataset.compareToggle, toggle.dataset.compareImage, toggle.dataset.compareTitle);
      return;
    }
    const remove = e.target.closest('[data-compare-remove]');
    if (remove) {
      e.preventDefault();
      Compare.items = Compare.items.filter((i) => i.handle !== remove.dataset.compareRemove);
      return;
    }
    const clear = e.target.closest('[data-compare-clear]');
    if (clear) {
      e.preventDefault();
      Compare.items = [];
      return;
    }
    const view = e.target.closest('[data-compare-view]');
    if (view) {
      e.preventDefault();
      const drawer = document.getElementById('CompareDrawer');
      if (drawer) {
        renderCompareDrawer(drawer);
        drawer.open();
      }
    }
  });

  async function renderCompareDrawer(drawer) {
    const body = drawer.querySelector('[data-compare-body]');
    if (!body) return;
    const items = Compare.items;
    if (!items.length) {
      body.innerHTML = '';
      return;
    }
    body.innerHTML = '<div class="infinite-trigger"><span class="spinner"></span></div>';
    try {
      const products = await Promise.all(
        items.map((item) => fetchJSON(`${rootUrl}products/${item.handle}.js`))
      );
      const specKeys = new Set();
      products.forEach((p) => (p.tags || []).forEach((tag) => {
        const [key] = tag.split(':');
        if (tag.includes(':')) specKeys.add(key);
      }));

      let html = '<div class="compare-table-wrap"><table class="compare-table"><thead><tr><th></th>';
      products.forEach((p) => {
        html += `<th><div class="compare-table__product">
          <img src="${esc(p.featured_image || '')}" alt="" loading="lazy" width="160" height="160">
          <a href="${rootUrl}products/${esc(p.handle)}"><b>${esc(p.title)}</b></a>
          <span class="price">${formatMoney(p.price)}</span>
        </div></th>`;
      });
      html += '</tr></thead><tbody>';
      html += '<tr><th>Vendor</th>' + products.map((p) => `<td>${esc(p.vendor || '—')}</td>`).join('') + '</tr>';
      html += '<tr><th>Type</th>' + products.map((p) => `<td>${esc(p.type || '—')}</td>`).join('') + '</tr>';
      html +=
        '<tr><th>Availability</th>' +
        products.map((p) => `<td>${p.available ? 'In stock' : 'Sold out'}</td>`).join('') +
        '</tr>';
      specKeys.forEach((key) => {
        html +=
          `<tr><th>${esc(key)}</th>` +
          products
            .map((p) => {
              const tag = (p.tags || []).find((t) => t.startsWith(key + ':'));
              return `<td>${esc(tag ? tag.split(':').slice(1).join(':') : '—')}</td>`;
            })
            .join('') +
          '</tr>';
      });
      html += '</tbody></table></div>';
      body.innerHTML = html;
    } catch (e) {
      body.innerHTML = '';
    }
  }

  document.addEventListener('DOMContentLoaded', () => Compare.render());
  Compare.render();

  /* ------------------------------------------------------------- wishlist */

  const Wishlist = {
    key: 'wishlist',
    get items() {
      return storage.get(Wishlist.key, []);
    },
    toggle(handle) {
      const items = Wishlist.items.slice();
      const index = items.indexOf(handle);
      index > -1 ? items.splice(index, 1) : items.push(handle);
      storage.set(Wishlist.key, items);
      Wishlist.render();
    },
    render() {
      const items = Wishlist.items;
      document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
        btn.classList.toggle('is-active', items.includes(btn.dataset.wishlistToggle));
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = items.length;
        el.hidden = items.length === 0;
      });
    },
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    Wishlist.toggle(btn.dataset.wishlistToggle);
  });
  document.addEventListener('DOMContentLoaded', () => Wishlist.render());

  /* ------------------------------------------------------- footer columns */

  /* The footer link columns are <details open> in the markup: that is the desktop
     state, and the state a shopper gets if this script never runs. On a phone they
     close, so four columns of links do not turn the footer into a long scroll.
     Toggled rather than re-rendered, so a column the shopper opened stays open until
     the viewport crosses back. */
  const footerColumns = () => {
    const phone = window.matchMedia('(max-width: 749px)');
    const apply = () => {
      document.querySelectorAll('.footer__collapse').forEach((column) => {
        column.open = !phone.matches;
      });
    };
    apply();
    phone.addEventListener('change', apply);
  };

  /*
   * A product accordion set to open by default is a desktop affordance: on a phone the
   * same three panels push the buy button below several screens of text. They start
   * closed at phone and tablet width. Only the state at load is touched — reopening one
   * is the shopper's own choice and nothing closes it again.
   */
  /*
   * An announcement longer than the bar used to wrap onto a second line, which doubled the
   * bar's height on a phone and clipped against the header. It scrolls instead: the track
   * carries two copies and travels exactly one copy plus the gap, so the loop has no seam.
   * Speed is fixed at 60px a second rather than a fixed duration, so a long message is not
   * made to race a short one. Nothing runs when the message fits, on a wide screen, or for
   * a visitor who asked for reduced motion — there it simply wraps, as before.
   */
  const topbarMarquee = () => {
    const box = document.querySelector('[data-topbar-message]');
    if (!box) return;
    const track = box.querySelector('.topbar__message-track');
    const copy = box.querySelector('.topbar__message-copy');
    if (!track || !copy) return;

    const apply = () => {
      box.classList.remove('topbar__message--scroll');
      track.style.removeProperty('--topbar-scroll-distance');
      track.style.removeProperty('--topbar-scroll-duration');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(max-width: 1023px)').matches) return;
      /* Measured with wrapping off: left to wrap, the copy's width can never exceed the
         box, and the overflow that triggers all of this would never be detected. */
      copy.style.whiteSpace = 'nowrap';
      const natural = copy.scrollWidth;
      copy.style.whiteSpace = '';
      if (natural <= box.clientWidth) return;

      box.classList.add('topbar__message--scroll');
      box.querySelectorAll('[aria-hidden="true"] a').forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const distance = natural + gap;
      track.style.setProperty('--topbar-scroll-distance', `${distance}px`);
      track.style.setProperty('--topbar-scroll-duration', `${Math.max(distance / 60, 8)}s`);
    };

    apply();
    let pending;
    window.addEventListener('resize', () => {
      clearTimeout(pending);
      pending = setTimeout(apply, 150);
    });
  };

  const productAccordions = () => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    document.querySelectorAll('.product__info details.accordion[open]').forEach((panel) => {
      panel.open = false;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', footerColumns);
    document.addEventListener('DOMContentLoaded', productAccordions);
    document.addEventListener('DOMContentLoaded', topbarMarquee);
  } else {
    footerColumns();
    productAccordions();
    topbarMarquee();
  }

  /* ------------------------------------------------------ recently viewed */

  const RecentlyViewed = {
    key: 'recently-viewed',
    limit: 12,
    push(handle) {
      if (!handle) return;
      let items = storage.get(RecentlyViewed.key, []).filter((h) => h !== handle);
      items.unshift(handle);
      storage.set(RecentlyViewed.key, items.slice(0, RecentlyViewed.limit));
    },
    get items() {
      return storage.get(RecentlyViewed.key, []);
    },
  };
  window.MeticRecentlyViewed = RecentlyViewed;

  class XRecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const current = this.dataset.currentHandle;
      const handles = RecentlyViewed.items.filter((h) => h !== current).slice(0, parseInt(this.dataset.limit || '8', 10));
      if (!handles.length) {
        const wrapper = this.closest('[data-section-wrapper]');
        (wrapper || this).remove();
        return;
      }
      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      try {
        const res = await fetch(
          `${routes.search_url}?type=product&options[unavailable_products]=last&q=${encodeURIComponent(query)}&section_id=${this.dataset.section}`
        );
        const doc = parseHTML(await res.text());
        const source = doc.querySelector('[data-recently-viewed-results]');
        const target = this.querySelector('[data-recently-viewed-results]');
        if (source && target) {
          target.innerHTML = source.innerHTML;
          emit('section:loaded', { target });
        } else {
          this.remove();
        }
      } catch (e) {
        this.remove();
      }
    }
  }
  customElements.define('x-recently-viewed', XRecentlyViewed);

  /* ------------------------------------------------------- <x-countdown> */

  class XCountdown extends HTMLElement {
    connectedCallback() {
      // Merchants enter "YYYY-MM-DD HH:MM"; only the T form parses everywhere
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = new Date(raw).getTime();
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.outputs = {
        days: this.querySelector('[data-cd-days]'),
        hours: this.querySelector('[data-cd-hours]'),
        minutes: this.querySelector('[data-cd-minutes]'),
        seconds: this.querySelector('[data-cd-seconds]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        if (this.dataset.hideOnEnd === 'true') this.remove();
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.outputs.days) this.outputs.days.textContent = pad(days);
      if (this.outputs.hours) this.outputs.hours.textContent = pad(hours);
      if (this.outputs.minutes) this.outputs.minutes.textContent = pad(minutes);
      if (this.outputs.seconds) this.outputs.seconds.textContent = pad(seconds);
    }
  }
  customElements.define('x-countdown', XCountdown);

  /* ---------------------------------------------------------- <x-facets> */

  class XFacets extends HTMLElement {
    connectedCallback() {
      // Delegated from the element itself: navigate() re-renders the form inside it,
      // and listeners bound to the form would go with it
      this.addEventListener(
        'input',
        debounce((e) => {
          if (e.target.type === 'number' || e.target.type === 'text') return;
          this.apply();
        }, 100)
      );
      this.addEventListener('change', (e) => {
        if (e.target.type === 'number' || e.target.type === 'text') this.apply();
      });
    }

    apply() {
      const form = this.querySelector('form');
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      // Strip empty values so the URL stays clean
      const cleaned = new URLSearchParams();
      params.forEach((value, key) => {
        if (value !== '') cleaned.append(key, value);
      });
      const url = `${window.location.pathname}?${cleaned.toString()}`;
      this.navigate(url);
    }

    async navigate(url, push = true) {
      const results = document.querySelector('[data-collection-results]');
      if (results) {
        results.classList.add('is-loading');
        results.setAttribute('aria-busy', 'true');
      }
      try {
        const res = await fetch(url);
        const doc = parseHTML(await res.text());
        /*
         * The collection renders its filters twice — a desktop sidebar and a mobile drawer —
         * so these selectors can match more than once. Zip the incoming nodes against the
         * live ones by document order; refreshing only the first match would leave the
         * drawer showing stale checked states and counts.
         */
        ['[data-collection-results]', '[data-facets-container]', '[data-active-facets]', '[data-collection-count]'].forEach(
          (selector) => {
            const sources = doc.querySelectorAll(selector);
            document.querySelectorAll(selector).forEach((target, i) => {
              if (sources[i]) target.innerHTML = sources[i].innerHTML;
            });
          }
        );
        if (push) window.history.pushState({}, '', url);
        emit('section:loaded', { target: document.querySelector('[data-collection-results]') });
        Compare.render();
        Wishlist.render();
        // The result count is itself a polite live region, so it announces the new total
      } finally {
        if (results) {
          results.classList.remove('is-loading');
          results.removeAttribute('aria-busy');
        }
      }
    }
  }
  customElements.define('x-facets', XFacets);

  /*
   * Back/forward is a page-level concern, not a per-element one. The collection renders
   * <x-facets> twice (desktop sidebar + mobile drawer) and navigate() already refreshes
   * every matching container in one pass, so one listener routed to the first instance
   * does the whole job — registering it per element fired two concurrent fetches whose
   * loading states cancelled each other out.
   */
  window.addEventListener('popstate', () => {
    const facets = document.querySelector('x-facets');
    if (!facets || typeof facets.navigate !== 'function') return;
    facets.navigate(window.location.href, false);
  });

  // The active-facet chips live outside <x-facets>, so the delegate has to be global
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-facet-remove]');
    if (!chip) return;
    const facets = document.querySelector('x-facets');
    if (!facets || typeof facets.navigate !== 'function') return;
    e.preventDefault();
    facets.navigate(chip.getAttribute('href'));
  });

  /* ------------------------------------------------- dual-handle price facet */

  /*
   * Two stacked <input type="range"> elements share one track. They carry no `name`, so
   * they never reach the querystring — the two number inputs next to them are the real
   * filter fields, and the sliders only write into those.
   *
   * Every listener below is registered on `document` in the CAPTURE phase and stops the
   * event there. <x-facets> filters on any `input` that is not a number/text field, which
   * would fire a fetch on every pixel of a drag; killing the event before it can bubble up
   * to <x-facets> is what makes "apply on release, not during the drag" true. The release
   * itself is a `change`, and that is where the number input is nudged so <x-facets> picks
   * the new bounds up through its normal path.
   */

  const priceParts = (root) => ({
    ceiling: parseFloat(root.dataset.priceCeiling) || 0,
    minRange: root.querySelector('[data-price-range="min"]'),
    maxRange: root.querySelector('[data-price-range="max"]'),
    minInput: root.querySelector('[data-price-input="min"]'),
    maxInput: root.querySelector('[data-price-input="max"]'),
  });

  // Handles must never swap sides: whichever one moved is pushed back to meet the other.
  const clampPriceHandles = (parts, moved) => {
    if (!parts.minRange || !parts.maxRange) return;
    const lo = parseFloat(parts.minRange.value);
    const hi = parseFloat(parts.maxRange.value);
    if (lo <= hi) return;
    if (moved === 'min') {
      parts.minRange.value = String(hi);
    } else {
      parts.maxRange.value = String(lo);
    }
  };

  const paintPriceSlider = (root) => {
    const parts = priceParts(root);
    if (!parts.ceiling || !parts.minRange || !parts.maxRange) return;
    const lo = (parseFloat(parts.minRange.value) / parts.ceiling) * 100;
    const hi = (parseFloat(parts.maxRange.value) / parts.ceiling) * 100;
    root.style.setProperty('--price-min-pct', `${lo}%`);
    root.style.setProperty('--price-max-pct', `${hi}%`);
  };

  // A handle parked at either end means "no bound", which keeps the URL clean.
  const priceRangesToInputs = (root) => {
    const parts = priceParts(root);
    if (!parts.ceiling) return;
    const lo = parseFloat(parts.minRange.value);
    const hi = parseFloat(parts.maxRange.value);
    if (parts.minInput) parts.minInput.value = lo > 0 ? String(lo) : '';
    if (parts.maxInput) parts.maxInput.value = hi < parts.ceiling ? String(hi) : '';
  };

  const priceInputsToRanges = (root) => {
    const parts = priceParts(root);
    if (!parts.ceiling || !parts.minRange || !parts.maxRange) return;
    const rawLo = parts.minInput ? parseFloat(parts.minInput.value) : NaN;
    const rawHi = parts.maxInput ? parseFloat(parts.maxInput.value) : NaN;
    let lo = isNaN(rawLo) ? 0 : Math.max(0, Math.min(parts.ceiling, Math.floor(rawLo)));
    let hi = isNaN(rawHi) ? parts.ceiling : Math.max(0, Math.min(parts.ceiling, Math.ceil(rawHi)));
    if (lo > hi) lo = hi;
    parts.minRange.value = String(lo);
    parts.maxRange.value = String(hi);
  };

  const priceRootFrom = (el) => (el && el.closest ? el.closest('[data-price-filter]') : null);

  document.addEventListener(
    'input',
    (e) => {
      const handle = e.target.closest && e.target.closest('[data-price-range]');
      if (!handle) return;
      e.stopPropagation(); // never let a drag reach <x-facets>
      const root = priceRootFrom(handle);
      if (!root) return;
      clampPriceHandles(priceParts(root), handle.dataset.priceRange);
      priceRangesToInputs(root);
      paintPriceSlider(root);
    },
    true
  );

  document.addEventListener(
    'change',
    (e) => {
      const handle = e.target.closest && e.target.closest('[data-price-range]');
      if (!handle) return;
      e.stopPropagation();
      const root = priceRootFrom(handle);
      if (!root) return;
      clampPriceHandles(priceParts(root), handle.dataset.priceRange);
      priceRangesToInputs(root);
      paintPriceSlider(root);
      // Release: hand the new bounds to <x-facets> through a field it already listens to.
      const parts = priceParts(root);
      const carrier = parts.maxInput || parts.minInput;
      if (carrier) carrier.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );

  // Typing in either number field drags the matching handle. <x-facets> ignores `input`
  // on number fields, so this stays quiet until the field's own `change` fires.
  document.addEventListener('input', (e) => {
    const field = e.target.closest && e.target.closest('[data-price-input]');
    if (!field) return;
    const root = priceRootFrom(field);
    if (!root) return;
    priceInputsToRanges(root);
    paintPriceSlider(root);
  });

  const paintAllPriceSliders = () => document.querySelectorAll('[data-price-filter]').forEach(paintPriceSlider);

  /* ------------------------------------------------------ collection toolbar */

  const VIEW_KEY = 'collection-view';

  const applyViewMode = () => {
    const mode = storage.get(VIEW_KEY, 'grid') === 'list' ? 'list' : 'grid';
    document.querySelectorAll('[data-product-grid]').forEach((grid) => {
      grid.classList.toggle('product-grid--list', mode === 'list');
    });
    document.querySelectorAll('[data-view-mode]').forEach((btn) => {
      const active = btn.dataset.viewMode === mode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  /*
   * Compare mode is OFF by default and is NOT remembered across page loads — it is a mode
   * you switch on for the browsing you are doing right now, and persisting it meant the
   * per-card checkboxes followed the shopper onto every other page.
   *
   * The one exception is an unfinished comparison: if products are already selected, the
   * mode stays on so the tray and its ticks are not silently lost on the next page.
   * The switch itself never clears the selection, so flipping it back restores it.
   */
  let compareModeOn = null;

  const applyCompareVisibility = () => {
    if (compareModeOn === null) compareModeOn = Compare.items.length > 0;
    const visible = compareModeOn;
    document.documentElement.classList.toggle('compare-controls-hidden', !visible);
    // The tray's visibility follows the mode, so re-render whenever the mode changes.
    if (window.MeticCompare && window.MeticCompare.render) window.MeticCompare.render();
    document.querySelectorAll('[data-compare-switch]').forEach((sw) => {
      sw.setAttribute('aria-checked', visible ? 'true' : 'false');
    });
  };

  document.addEventListener('click', (e) => {
    const viewBtn = e.target.closest('[data-view-mode]');
    if (viewBtn) {
      storage.set(VIEW_KEY, viewBtn.dataset.viewMode === 'list' ? 'list' : 'grid');
      applyViewMode();
      return;
    }
    const sw = e.target.closest('[data-compare-switch]');
    if (!sw) return;
    compareModeOn = sw.getAttribute('aria-checked') !== 'true';
    applyCompareVisibility();
  });

  /*
   * The toolbar sits outside the four containers <x-facets> re-renders, so its selected
   * sort would keep showing the page-load value after a filter run — re-read it from the
   * URL <x-facets> pushed.
   */
  const syncSortSelects = () => {
    const value = new URL(window.location.href).searchParams.get('sort_by');
    if (!value) return;
    document.querySelectorAll('[data-sort-select]').forEach((select) => {
      if (select.value !== value) select.value = value;
    });
  };

  document.addEventListener('change', (e) => {
    const select = e.target.closest && e.target.closest('[data-sort-select]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    const facets = document.querySelector('x-facets');
    if (facets && typeof facets.navigate === 'function') {
      facets.navigate(url.pathname + url.search);
    } else {
      window.location.assign(url.toString());
    }
  });

  const initCollectionUi = () => {
    applyViewMode();
    applyCompareVisibility();
    syncSortSelects();
    paintAllPriceSliders();
  };

  document.addEventListener('DOMContentLoaded', initCollectionUi);
  window.addEventListener('popstate', syncSortSelects);
  on('section:loaded', initCollectionUi);
  initCollectionUi();

  /* --------------------------------------------------- <x-infinite-scroll> */

  class XInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'scroll';
      this.button = this.querySelector('[data-load-more]');
      if (this.button) this.button.addEventListener('click', () => this.load());
      if (this.mode === 'scroll' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((entry) => entry.isIntersecting && this.load()),
          { rootMargin: '400px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');
      try {
        const res = await fetch(this.dataset.nextUrl);
        const doc = parseHTML(await res.text());
        const grid = document.querySelector('[data-product-grid]');
        const newItems = doc.querySelectorAll('[data-product-grid] > *');
        if (grid && newItems.length) {
          /*
           * Sold-out cards collect at the end of the grid, so an arriving in-stock card
           * belongs above them, not after. Shopify offers no collection sort that knows
           * about stock and Liquid can only group one page at a time; this carries the
           * grouping across the pages the shopper loads. The anchor is read once, before
           * anything moves, and inserting before it keeps the shift below the viewport.
           */
          const soldOutAnchor = grid.querySelector('[data-unavailable]');
          newItems.forEach((item) => {
            const unavailable = item.hasAttribute && item.hasAttribute('data-unavailable');
            if (!unavailable && soldOutAnchor) grid.insertBefore(item, soldOutAnchor);
            else grid.appendChild(item);
          });
          emit('section:loaded', { target: grid });
          Compare.render();
          Wishlist.render();
          announce((strings.productsLoaded || '[count] more products loaded').replace('[count]', newItems.length));
        }
        const nextTrigger = doc.querySelector('x-infinite-scroll');
        const nextUrl = nextTrigger && nextTrigger.dataset.nextUrl;
        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
        } else {
          if (this.observer) this.observer.disconnect();
          this.remove();
        }
      } catch (e) {
        /* leave the trigger in place so the user can retry */
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('x-infinite-scroll', XInfiniteScroll);

  /* ------------------------------------------------------- <x-sticky-atc> */

  class XStickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.anchor || '[data-product-buy]');
      this.phone = window.matchMedia('(max-width: 1023px)');

      /*
       * On a phone the bar is pinned from the moment the page loads rather than waiting for
       * the buy button to scroll out of sight: the price and the action stay within thumb
       * reach through the whole page. On a wider screen the button sits in view beside the
       * gallery, so there the bar still earns its place only once that button has passed.
       */
      this.applyMode = () => {
        if (this.phone.matches) {
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
          this.classList.add('is-visible');
          return;
        }
        this.classList.remove('is-visible');
        if (this.observer || !anchor || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
          { threshold: 0 }
        );
        this.observer.observe(anchor);
      };

      this.applyMode();
      this.phone.addEventListener('change', this.applyMode);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.phone && this.applyMode) this.phone.removeEventListener('change', this.applyMode);
    }
  }
  customElements.define('x-sticky-atc', XStickyAtc);

  /* --------------------------------------------------------- <x-gallery> */

  class XGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.viewport = this.querySelector('.carousel__viewport');

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const id = thumb.dataset.thumb;
          const media = this.slides.find((s) => s.dataset.mediaId === id);
          if (!media) return;
          if (this.viewport) {
            /*
             * Measure through rects, not offsetLeft. data-media-id sits on the media item
             * nested inside .carousel__slide, so its offsetParent is not the viewport and
             * `offsetLeft - viewport.offsetLeft` resolved to 0 — every thumbnail scrolled
             * back to the first slide. Rects are relative to the viewport regardless of
             * where the positioned ancestor happens to be.
             */
            const target = media.closest('.carousel__slide') || media;
            const delta = target.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;
            smoothScrollX(this.viewport, this.viewport.scrollLeft + delta);
          } else {
            media.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          this.setActive(id);
        });
      });

      if (this.dataset.zoom === 'true') {
        this.addEventListener('click', (e) => {
          const img = e.target.closest('[data-zoom-src]');
          if (!img) return;
          this.openZoom(img.dataset.zoomSrc, img.alt);
        });
      }

      if ('IntersectionObserver' in window && this.viewport) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setActive(entry.target.dataset.mediaId);
            });
          },
          { root: this.viewport, threshold: 0.6 }
        );
        this.slides.forEach((slide) => io.observe(slide));
      }
    }

    setActive(id) {
      this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', thumb.dataset.thumb === id ? 'true' : 'false'));
    }

    openZoom(src, alt) {
      const lightbox = getZoomLightbox();
      const img = lightbox.querySelector('img');
      img.src = src;
      img.alt = alt || '';
      lightbox.classList.add('is-open');
      document.body.classList.add('drawer-open');
    }
  }
  customElements.define('x-gallery', XGallery);

  // Built once for the whole page, so its listeners are registered once too
  let zoomLightbox = null;
  const getZoomLightbox = () => {
    if (zoomLightbox) return zoomLightbox;
    // A theme section is free to ship its own #ZoomLightbox markup; either way the close
    // handlers are wired to whichever element we end up using, exactly once.
    let element = document.getElementById('ZoomLightbox');
    if (!element) {
      element = document.createElement('div');
      element.id = 'ZoomLightbox';
      element.className = 'zoom-lightbox';
      element.innerHTML =
        '<button type="button" class="zoom-lightbox__close" aria-label="Close"><svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button><img alt="">';
      document.body.appendChild(element);
    } else if (!element.querySelector('img')) {
      element.appendChild(document.createElement('img'));
    }
    zoomLightbox = element;
    const closeZoom = () => {
      element.classList.remove('is-open');
      if (!document.querySelector('x-drawer.is-open')) document.body.classList.remove('drawer-open');
    };
    element.addEventListener('click', (e) => {
      if (e.target === element || e.target.closest('.zoom-lightbox__close')) closeZoom();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && element.classList.contains('is-open')) closeZoom();
    });
    return element;
  };

  /* --------------------------------------------------- <x-before-after> */

  class XBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.wrap = this.querySelector('.before-after');
      if (!this.wrap) return;
      this.handle = this.wrap.querySelector('.before-after__handle');
      this.pct = 50;

      this.setPct(this.pct);

      const moveTo = (clientX) => {
        const rect = this.wrap.getBoundingClientRect();
        if (!rect.width) return;
        this.setPct(((clientX - rect.left) / rect.width) * 100);
      };

      let dragging = false;
      this.wrap.addEventListener('pointerdown', (e) => {
        dragging = true;
        moveTo(e.clientX);
        this.wrap.setPointerCapture(e.pointerId);
      });
      this.wrap.addEventListener('pointermove', (e) => dragging && moveTo(e.clientX));
      ['pointerup', 'pointercancel'].forEach((evt) =>
        this.wrap.addEventListener(evt, () => {
          dragging = false;
        })
      );

      // Keyboard parity: the handle is a focusable slider, so arrows nudge and Home/End jump.
      if (this.handle) {
        this.handle.addEventListener('keydown', (e) => {
          const big = e.key === 'PageUp' || e.key === 'PageDown';
          const step = e.shiftKey || big ? 10 : 1;
          let next = this.pct;
          switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
            case 'PageDown':
              next = this.pct - step;
              break;
            case 'ArrowRight':
            case 'ArrowUp':
            case 'PageUp':
              next = this.pct + step;
              break;
            case 'Home':
              next = 0;
              break;
            case 'End':
              next = 100;
              break;
            default:
              return;
          }
          e.preventDefault();
          this.setPct(next);
        });
      }
    }

    setPct(value) {
      const pct = Math.min(100, Math.max(0, value));
      this.pct = pct;
      this.wrap.style.setProperty('--position', pct + '%');
      if (this.handle) this.handle.setAttribute('aria-valuenow', Math.round(pct));
    }
  }
  customElements.define('x-before-after', XBeforeAfter);

  /* -------------------------------------------------------- <x-bundle> */

  class XBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-bundle-add]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.addAll());
    }

    async addAll() {
      const items = Array.from(this.querySelectorAll('[data-bundle-variant]'))
        .map((el) => ({ id: parseInt(el.dataset.bundleVariant, 10), quantity: parseInt(el.dataset.bundleQty || '1', 10) }))
        .filter((item) => item.id);
      if (!items.length) return;
      this.button.classList.add('is-loading');
      this.button.disabled = true;
      try {
        await Cart.add(items);
        toast(strings.added || 'Added to cart');
      } catch (err) {
        toast(err && err.description ? err.description : 'Error', 'error');
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }
  }
  customElements.define('x-bundle', XBundle);

  /* ---------------------------------------------------------- <x-popup> */

  class XPopup extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('x-drawer');
      if (!this.drawer) return;
      const key = 'popup:' + (this.dataset.key || 'default');
      const frequency = parseInt(this.dataset.frequencyDays || '7', 10);
      const seen = storage.get(key, 0);
      if (seen && Date.now() - seen < frequency * 86400000) return;

      const delay = parseInt(this.dataset.delay || '5', 10) * 1000;
      this.timer = setTimeout(() => {
        this.drawer.open();
        storage.set(key, Date.now());
      }, delay);

      this.drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) storage.set(key, Date.now());
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }
  }
  customElements.define('x-popup', XPopup);

  /* ------------------------------------------------------ <x-back-to-top> */

  class XBackToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      this.onScroll = () => this.button.classList.toggle('is-visible', window.scrollY > 600);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('x-back-to-top', XBackToTop);

  /* ------------------------------------------------- <x-shipping-estimate> */

  class XShippingEstimate extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-estimate]');
      this.output = this.querySelector('[data-estimate-results]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.estimate());
    }

    async estimate() {
      const countryField = this.querySelector('[name="country"]');
      const zipField = this.querySelector('[name="zip"]');
      if (!countryField || !zipField) return;
      this.button.classList.add('is-loading');
      try {
        // Country + postal code only: the cart form asks for no province, and posting an
        // always-empty shipping_address[province] just narrowed the rates Shopify returned.
        const params = new URLSearchParams({
          'shipping_address[country]': countryField.value,
          'shipping_address[zip]': zipField.value,
        });
        const data = await fetchJSON(`${rootUrl}cart/shipping_rates.json?${params}`);
        const rates = data.shipping_rates || [];
        this.output.innerHTML = rates.length
          ? '<ul>' + rates.map((r) => `<li>${r.name} — ${formatMoney(parseFloat(r.price) * 100)}</li>`).join('') + '</ul>'
          : '<p class="subtext">—</p>';
      } catch (err) {
        this.output.innerHTML = '<p class="form-status form-status--error">Unable to calculate rates.</p>';
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('x-shipping-estimate', XShippingEstimate);

  /* ---------------------------------------------- <x-product-recommendations> */

  class XProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = `${routes.product_recommendations_url}?section_id=${this.dataset.section}&product_id=${this.dataset.productId}&limit=${this.dataset.limit || 8}&intent=${this.dataset.intent || 'related'}`;
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const doc = parseHTML(text);
          const source = doc.querySelector('[data-recommendations-inner]');
          if (source && source.innerHTML.trim()) {
            this.innerHTML = source.innerHTML;
            emit('section:loaded', { target: this });
            Compare.render();
            Wishlist.render();
          } else {
            this.remove();
          }
        })
        .catch(() => this.remove());
    }
  }
  customElements.define('x-product-recommendations', XProductRecommendations);

  /* ----------------------------------------------------- <x-quick-order> */

  class XQuickOrder extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-quick-order-submit]');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.submit());
    }

    async submit() {
      const items = Array.from(this.querySelectorAll('[data-quick-order-row]'))
        .map((row) => {
          // Sold-out rows render no stepper
          const input = row.querySelector('input[type="number"]');
          if (!input) return null;
          const qty = parseInt(input.value || '0', 10);
          return qty > 0 ? { id: parseInt(row.dataset.variantId, 10), quantity: qty } : null;
        })
        .filter(Boolean);
      if (!items.length) return;
      this.button.classList.add('is-loading');
      try {
        await Cart.add(items);
        toast(strings.added || 'Added to cart');
        this.querySelectorAll('input[type="number"]').forEach((input) => (input.value = 0));
      } catch (err) {
        toast(err && err.description ? err.description : 'Error', 'error');
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('x-quick-order', XQuickOrder);

  /* ---------------------------------------------------------- localization */

  document.addEventListener('click', (e) => {
    const item = e.target.closest('[data-localization-value]');
    if (!item) return;
    const form = item.closest('form');
    if (!form) return;
    const input = form.querySelector('input[name="country_code"], input[name="locale_code"]');
    if (input) {
      input.value = item.dataset.localizationValue;
      form.submit();
    }
  });

  /*
   * Close dropdown-style <details> on an outside click — OPT IN, via the attribute.
   * This was previously a blocklist of classes that should be left alone, which meant any
   * new <details> silently inherited dropdown behaviour: the filter groups collapsed on
   * every click anywhere, and opening one group closed all the others.
   */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-close-on-outside][open]').forEach((details) => {
      if (details.contains(e.target)) return;
      details.removeAttribute('open');
    });
  });

  /* ------------------------------------------- <x-cart-recommendations> */

  class XCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.load();
      // The best complement depends on the cart's contents, so refresh on every change.
      this.onCart = () => this.load();
      on('cart:updated', this.onCart);
      on('cart:added', this.onCart);
    }

    async load() {
      const productId = this.dataset.productId;
      if (!productId) return;
      const limit = this.dataset.limit || 4;

      const fetchIntent = async (intent) => {
        const res = await fetch(
          `${routes.product_recommendations_url}?section_id=cart-recommendations&product_id=${productId}&limit=${limit}&intent=${intent}`
        );
        const source = parseHTML(await res.text()).querySelector('[data-cart-recos-inner]');
        return source && source.innerHTML.trim() ? source.innerHTML : '';
      };

      try {
        /*
         * Complementary first — a merchant who has curated pairings in Search & Discovery
         * gets those. Most stores have not, and that intent returns nothing at all, so fall
         * back to `related` rather than showing an empty panel.
         */
        let html = await fetchIntent('complementary');
        if (!html) html = await fetchIntent('related');
        this.innerHTML = html;
      } catch (e) {
        this.innerHTML = '';
      }
    }
  }
  customElements.define('x-cart-recommendations', XCartRecommendations);

  /*
   * Discount codes live in the cart JSON, not in Liquid. `cart.discount_codes` renders
   * empty for a code the cart is holding but cannot apply — /cart.js returns
   * [{"code":"BIENVENUE","applicable":false}] for the very same cart. Since showing that
   * state is the point, the rows are drawn here from the JSON.
   */
  const Discounts = {
    key: 'metic:discount-codes',

    codes(cart) {
      return (cart && cart.discount_codes) || [];
    },

    /*
     * The saving comes from cart_level_discount_applications, matched to the code by
     * title — Shopify names a code-driven application after the code itself. The row
     * carries it, so the separate discount line the totals used to print is gone and the
     * shopper reads the code once.
     *
     * The two endpoints disagree on the shape. /cart.js returns the application flat:
     *   { title, total_allocated_amount, ... }
     * while /cart/update.js wraps it:
     *   { amount, discount_application: { title, total_allocated_amount, ... } }
     * Reading only the flat one left the amount blank for the whole moment after applying
     * a code, until some later render happened to run off /cart.js.
     */
    amountFor(cart, code) {
      const apps = (cart && cart.cart_level_discount_applications) || [];
      const hit = apps.find((a) => {
        const app = a.discount_application || a;
        return (app.title || '').toUpperCase() === code.toUpperCase();
      });
      if (!hit) return 0;
      const app = hit.discount_application || hit;
      return hit.amount != null ? hit.amount : app.total_allocated_amount || 0;
    },

    minimum() {
      const cents = Number(Theme.couponMinimum || 0);
      return cents > 0 ? cents : 0;
    },

    /*
     * The bar shows the nearest goal the shopper has not reached. A code waiting on a
     * minimum takes precedence over free shipping — it is the thing they just asked for —
     * and once the code applies the bar hands back to shipping, or goes altogether if
     * shipping is covered too.
     *
     * Built here rather than in Liquid because whether a code is pending is only knowable
     * from the cart JSON, and Liquid renders no bar at all once shipping is met.
     */
    renderProgress(cart, list) {
      const host = document.querySelector('[data-coupon-progress]');
      if (!host) return;
      const minimum = Discounts.minimum();
      const pending = list.find((d) => d.applicable === false);
      const shippingBars = document.querySelectorAll('[data-shipping-bar]');

      if (!pending || !minimum) {
        host.innerHTML = '';
        host.hidden = true;
        shippingBars.forEach((b) => {
          b.hidden = false;
        });
        return;
      }

      const total = cart.total_price || 0;
      const remaining = Math.max(minimum - total, 0);
      const pct = Math.min(Math.round((total / minimum) * 1000) / 10, 100);

      host.hidden = false;
      host.innerHTML = '';
      const bar = document.createElement('div');
      bar.className = 'shipping-bar shipping-bar--plain';
      const text = document.createElement('p');
      text.className = 'shipping-bar__text';
      text.textContent = sayWith('couponProgress', {
        amount: formatMoney(remaining),
        code: pending.code,
      });
      const track = document.createElement('div');
      track.className = 'shipping-bar__track';
      const fill = document.createElement('div');
      fill.className = 'shipping-bar__fill';
      fill.style.width = pct + '%';
      track.appendChild(fill);
      bar.appendChild(text);
      bar.appendChild(track);
      host.appendChild(bar);

      // One bar at a time: the code's goal replaces the shipping one while it is unmet.
      shippingBars.forEach((b) => {
        b.hidden = true;
      });
    },

    render(cart) {
      const list = Discounts.codes(cart);
      document.querySelectorAll('[data-cart-discounts]').forEach((ul) => {
        ul.innerHTML = '';
        ul.hidden = list.length === 0;
        list.forEach((d) => {
          const pending = d.applicable === false;
          const saved = Discounts.amountFor(cart, d.code);

          const li = document.createElement('li');
          li.className = 'cart-discount' + (pending ? ' cart-discount--pending' : '');
          li.dataset.discountCode = d.code;
          if (pending) li.setAttribute('data-discount-pending', '');

          const body = document.createElement('span');
          body.className = 'cart-discount__body';

          const head = document.createElement('span');
          head.className = 'cart-discount__head';
          const label = document.createElement('span');
          label.className = 'cart-discount__label';
          label.textContent = decodeEntities(strings.couponLabel || '');
          const code = document.createElement('span');
          code.className = 'cart-discount__code';
          code.textContent = d.code;
          head.appendChild(label);
          head.appendChild(code);
          body.appendChild(head);

          /*
           * A pending row says nothing beyond its code when the progress bar can carry the
           * message: how far off the cart is, in money, beats a paragraph about conditions.
           * Without a configured minimum there is no bar to draw, so the short state line
           * comes back — never the full note, which is what made the drawer so busy.
           */
          if (pending && !Discounts.minimum()) {
            const state = document.createElement('span');
            state.className = 'cart-discount__state';
            state.textContent = decodeEntities(strings.couponNotApplicable || '');
            body.appendChild(state);
          }
          if (!pending) li.classList.add('cart-discount--active');

          li.appendChild(body);

          if (!pending && saved > 0) {
            const amount = document.createElement('span');
            amount.className = 'cart-discount__amount';
            amount.textContent = '-' + formatMoney(saved);
            li.appendChild(amount);
          }

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'cart-discount__remove';
          remove.setAttribute('data-discount-remove', d.code);
          remove.setAttribute('aria-label', say('couponRemoveLabel', d.code));
          remove.innerHTML = icons.close || '&times;';
          li.appendChild(remove);

          ul.appendChild(li);
        });
      });
      Discounts.renderProgress(cart, list);
      return list;
    },

    remember(list) {
      try {
        localStorage.setItem(Discounts.key, JSON.stringify(list.map((d) => d.code)));
      } catch (e) {
        /* private mode: the worst case is announcing the same code twice */
      }
    },

    seen() {
      try {
        return JSON.parse(localStorage.getItem(Discounts.key) || '[]');
      } catch (e) {
        return [];
      }
    },

    // Announces anything that arrived since this browser last looked — a code applied by a
    // /discount/ permalink never passes through the coupon form.
    announceNew(cart) {
      const list = Discounts.codes(cart);
      const before = Discounts.seen();
      list
        .filter((d) => !before.includes(d.code))
        .forEach((d) => {
          if (d.applicable === false) toast(say('couponPending', d.code), 'error');
          else toast(say('couponApplied', d.code));
        });
      Discounts.remember(list);
    },

    async init() {
      try {
        const cart = await fetchJSON(routes.cart_url + '.js');
        Discounts.render(cart);
        Discounts.announceNew(cart);
      } catch (e) {
        /* nothing to show */
      }
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Discounts.init());
  } else {
    Discounts.init();
  }

  /* ------------------------------------------------------ <x-coupon-form> */

  class XCouponForm extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="discount"]');
      const apply = this.querySelector('[data-coupon-apply]');
      if (apply) apply.addEventListener('click', () => this.apply());
      /*
       * Delegated from the document: the chips live in the drawer footer, outside this
       * element, and both are replaced wholesale every time the section re-renders.
       */
      if (!XCouponForm.bound) {
        XCouponForm.bound = true;
        document.addEventListener('click', (e) => {
          const btn = e.target.closest && e.target.closest('[data-discount-remove]');
          if (!btn) return;
          e.preventDefault();
          const form = document.querySelector('x-coupon-form');
          if (form) form.removeCode(btn.dataset.discountRemove);
        });
      }
      if (this.input) {
        this.input.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          this.apply();
        });
      }
    }

    /*
     * /cart/update.js takes a `discount` parameter: a comma-separated list that REPLACES
     * whatever the cart held, with an empty string clearing it. So every change sends the
     * full list, and removing one code means sending the others back.
     */
    codes() {
      return Array.from(document.querySelectorAll('[data-cart-discounts] [data-discount-code]'))
        .map((el) => el.dataset.discountCode)
        .filter(Boolean);
    }

    /*
     * /cart/update.js takes `discount`: a comma-separated list that REPLACES whatever the
     * cart held, empty to clear. It answers with the cart, so the result is read straight
     * from the response — no second request, and no guessing from re-rendered markup.
     */
    async submit(list) {
      this.classList.add('is-loading');
      try {
        const cart = await Cart.update({ discount: list.join(',') });
        const codes = Discounts.render(cart);
        Discounts.remember(codes);
        return codes;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    /*
     * Shopify stores whatever it is given — "c" lands in the cart as a code awaiting its
     * conditions, indistinguishable from a real one. Normalising kills the duplicates and
     * the stray whitespace; the allowlist, when the merchant fills it in, is the only way
     * to actually refuse a typo.
     */
    normalise(value) {
      return (value || '').trim().replace(/\s+/g, '').toUpperCase();
    }

    allowed(code) {
      const raw = (Theme.couponAllowlist || '').trim();
      if (!raw) return true;
      return raw
        .split(',')
        .map((c) => this.normalise(c))
        .filter(Boolean)
        .includes(code);
    }

    async apply() {
      const code = this.normalise(this.input && this.input.value);
      if (!code) return;
      if (!this.allowed(code)) {
        toast(say('couponInvalid', code), 'error');
        if (this.input) this.input.select();
        return;
      }
      const existing = this.codes();
      if (existing.some((c) => c.toUpperCase() === code)) {
        // Already held — re-sending would just reorder the list.
        if (this.input) this.input.value = '';
        return;
      }
      if (existing.length >= 5) {
        toast(say('couponInvalid', code), 'error');
        return;
      }
      try {
        const after = await this.submit(existing.concat(code));
        const kept = after.find((d) => d.code.toLowerCase() === code.toLowerCase());
        if (!kept) {
          /*
           * Rarely reached: Shopify accepts ANY string as a discount code and answers
           * applicable:false, so a typo is indistinguishable from a real code whose
           * conditions are not met — both take the branch below. This one is left for the
           * case where the cart comes back without the code at all.
           */
          toast(say('couponInvalid', code), 'error');
          return;
        }
        if (this.input) this.input.value = '';
        if (kept.applicable === false) toast(say('couponPending', kept.code), 'error');
        else toast(say('couponApplied', kept.code));
      } catch (e) {
        toast(say('couponInvalid', code), 'error');
      }
    }

    async removeCode(code) {
      const remaining = this.codes().filter((c) => c.toLowerCase() !== code.toLowerCase());
      try {
        await this.submit(remaining);
        toast(say('couponRemoved', code));
      } catch (e) {
        /* the cart is unchanged */
      }
    }
  }
  customElements.define('x-coupon-form', XCouponForm);

  /* ------------------------------------------------------ cart sub-panels */

  /*
   * Chips open a panel that slides up over the cart rather than expanding inline, so the
   * cart stays where it was and the footer never jumps. Only one is open at a time.
   */
  /*
   * The open/closed state is written inline rather than left to the class alone. The class
   * rule is correct and deployed, but the panel was observed keeping its closed computed
   * values with .is-open applied; inline styles remove any doubt about the cascade and
   * still animate, since the transition lives on the base rule.
   */
  const setSubdrawerState = (panel, open) => {
    panel.classList.toggle('is-open', open);
  };

  const closeSubdrawers = (root) => {
    const scope = root || document;
    scope.querySelectorAll('.cart-subdrawer').forEach((p) => setSubdrawerState(p, false));
    scope.querySelectorAll('.cart-subdrawer-scrim').forEach((sc) => sc.classList.remove('is-open'));
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-subdrawer-open]');
    if (opener) {
      e.preventDefault();
      const panel = document.getElementById(opener.getAttribute('data-subdrawer-open'));
      if (!panel) return;
      const host = panel.closest('.drawer__panel') || document;
      closeSubdrawers(host);
      setSubdrawerState(panel, true);
      const scrim = host.querySelector('.cart-subdrawer-scrim');
      if (scrim) scrim.classList.add('is-open');
      /*
       * Focus after the slide-up finishes. The panel is still visibility:hidden while it
       * transitions, and focusing a hidden element silently does nothing.
       */
      const field = panel.querySelector('textarea, input, select');
      if (field) {
        const focusField = () => field.focus({ preventScroll: true });
        panel.addEventListener('transitionend', focusField, { once: true });
        setTimeout(focusField, 400); // fallback if the transition is suppressed
      }
      return;
    }

    if (e.target.closest('[data-subdrawer-close]') || e.target.closest('[data-subdrawer-scrim]')) {
      e.preventDefault();
      closeSubdrawers(document);
    }
  });

  // Escape closes the sub-panel first, leaving the cart itself open.
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return;
      if (!document.querySelector('.cart-subdrawer.is-open')) return;
      e.stopPropagation();
      closeSubdrawers(document);
    },
    true
  );

  on('drawer:close', () => closeSubdrawers(document));


  /* ---------------------------------------------------- Shopify editor hooks */

  document.addEventListener('shopify:section:load', (e) => {
    emit('section:loaded', { target: e.target });
    Compare.render();
    Wishlist.render();
  });

  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('x-drawer');
    if (drawer && drawer.dataset.editorOpen === 'true') drawer.open();
  });

  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = e.target.querySelector('x-drawer');
    if (drawer && drawer.classList.contains('is-open')) drawer.close();
  });
})();
